import React, { useEffect, useMemo, useState } from 'react';
import { HbrInput, HbrTextarea } from '@harbor/elements-react-standalone';
import { FormComponentChange, FormComponentType, FormErrorType, useTestCaseFormContext } from './ValidationTestCaseFormContext';
import { FormSectionUseCase } from './FormSectionUseCase';
import StbButton from '../../../../security-toolbox-ui/StbButton';
import { useModal } from '../../../../security-toolbox-ui/StbModalContext';
import StbAccordion from '../../../../security-toolbox-ui/StbAccordion';
import { useParams } from 'react-router-dom';
import { useFormAddPovOutcomeMutation, useFormDeletePovOutcomeMutation, useFormUpdatePovOutcomeMutation } from '../../../../../hooks/opportunity-pov-hooks';
import { PoVOutcome } from '../../../../../types/opportunity-pov-items';
import { useFetchPov } from '../../../opportunityDetailHelpers';
import { unicodeToPlain } from '../../../../../support/utils';
import StbModal from '../../../../security-toolbox-ui/StbModal';

const MAX_TITLE_LENGTH = 40;
const MAX_DESCRIPTION_LENGTH = 1000;

const FormSectionOutcome: React.FC<{
  formSectionId: string
}> = ({
        formSectionId
      }) => {
  const { id: opportunityId } = useParams();
  const { data: pov } = useFetchPov({ Id: opportunityId });
  const { openModal, closeModal } = useModal();
  const { getFormItem, setFormItemSaved, updateFormItem, addFormItem, removeFormItem, setFormItem, setFormError } = useTestCaseFormContext();
  const outcome = useMemo(() => getFormItem(formSectionId), [getFormItem, formSectionId]);

  const [isOpen, setIsOpen] = useState<boolean>(true);
  const [isEditing, setIsEditing] = useState<boolean>(false);

  useEffect(() => {
    if (outcome?.change === FormComponentChange.Added) {
      setIsEditing(true);
    }
  }, [outcome?.change]);

  const prev = useMemo(() => isEditing ? outcome?.data : undefined, [isEditing, outcome?.data]);

  const { mutateAsync: createOutcome } = useFormAddPovOutcomeMutation(opportunityId);
  const { mutateAsync: updateOutcome } = useFormUpdatePovOutcomeMutation(opportunityId);
  const { mutateAsync: deleteOutcome } = useFormDeletePovOutcomeMutation(opportunityId);

  const handleInputChange = (field: string, value: string) => {
    updateFormItem(formSectionId, field, value);
    if (outcome?.error) {
      setFormError(formSectionId, '');
    }
  };

  const handleDeleteOutcome = async () => {
    const usecases = outcome?.children || [];
    const testcases = outcome?.children?.flatMap(usecase => getFormItem(usecase)?.children) || [];

    try {
      await deleteOutcome(outcome?.data as PoVOutcome);
      removeFormItem([...testcases, ...usecases, formSectionId]);
      return;
    } catch (error) {
      setFormError(formSectionId, 'Failed to delete outcome', FormErrorType.Api);
      console.error('Error deleting outcome:', error);
    }
  };

  const handleOpenDeleteConfirmModal = () => {
    openModal({
      content: () => (
        <StbModal
          open={true}
          role="delete"
          onSubmit={handleDeleteOutcome}
          onClose={async () => closeModal()}
        >
          <div>Are you sure you want to delete this outcome{outcome?.children?.length > 0 ? ' and all child items?' : '?'}</div>
        </StbModal>
      ),
    });
  };

  const handleCancelClick = () => {
    if (outcome?.change === FormComponentChange.Added) {
      removeFormItem(formSectionId);
    } else {
      setIsEditing(false);
      setFormItem(formSectionId, prev);
    }
    setFormError(formSectionId, undefined, undefined);
  };

  const handleAddUseCaseButtonClick = () => {
    addFormItem(FormComponentType.UseCase, outcome.formSectionId);
  };

  const handleUpdateOutcomeClick = () => {
    setIsEditing(true);
    setIsOpen(true);
  };

  const handleSaveClick = async () => {
    if (!outcome?.data?.title || outcome?.data?.title.trim().length === 0) {
      setFormError(formSectionId, 'Title is required', FormErrorType.Form);
      return;
    }

    if (outcome?.change === FormComponentChange.NoChange) {
      setIsEditing(false);
      return;
    }

    try {
      const payload = {
        ...outcome.data,
        title: outcome.data.title?.trim(),
        description: outcome.data.description?.trim() || '',
        pov: pov?.id,
        type: 'Outcome'
      } as PoVOutcome;

      let result;
      if (outcome?.change === FormComponentChange.Added) {
        result = await createOutcome(payload);
      } else if (outcome?.change === FormComponentChange.Updated) {
        result = await updateOutcome(payload);
      }

      setIsEditing(false);
      setFormItemSaved(formSectionId, result);
      setFormError(formSectionId, undefined, undefined);
    } catch (error) {
      setFormError(formSectionId, 'Failed to save outcome', FormErrorType.Api);
      console.error('Error saving outcome:', error);
    }
  };

  return (
    <StbAccordion
      isOpen={isOpen}
      toggleIsOpen={setIsOpen}
      interactive={!isEditing}
      className={isEditing ? 'w-full border-2 border-gray-500 rounded-lg' : 'w-full'}
      headerContent={
        <div className="flex flex-row justify-between items-start gap-4 w-full">
          <div className="flex-1 min-w-0">
            {isEditing ? (
              <HbrInput
                value={outcome?.data?.title}
                placeholder="Outcome title"
                className="grow"
                maxlength={MAX_TITLE_LENGTH}
                onHbr-input={(e) => {
                  e.stopPropagation();
                  handleInputChange('title', unicodeToPlain(e.target.value));
                }}
                error={outcome?.error?.length > 0}
              >
                {outcome?.error && (
                  <div slot="help-text" style={{ color: 'var(--hbr-color-danger)' }}>
                    {outcome?.error}
                  </div>
                )}
              </HbrInput>
            ) : (
              <div className="text-base font-semibold break-words">{outcome?.data?.title}</div>
            )}
          </div>

          <div className="flex items-center justify-end flex-shrink-0">
            {isEditing ? (
              <div>
                <StbButton
                  iconOnly={true}
                  role="save"
                  size="small"
                  variant="text"
                  className="mr-1"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleSaveClick();
                  }}
                />
                <StbButton
                  iconOnly={true}
                  role="cancel"
                  size="small"
                  variant="text"
                  className="mr-1"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleCancelClick();
                  }}
                />
              </div>
            ) : (
              <div className="flex-grow-0 min-w-fit">
                <StbButton
                  iconOnly={true}
                  role="update"
                  size="small"
                  variant="text"
                  className="mr-1"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleUpdateOutcomeClick();
                  }}
                />
                <StbButton
                  iconOnly={true}
                  role="delete"
                  size="small"
                  variant="text"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleOpenDeleteConfirmModal();
                  }}
                />
              </div>
            )}
          </div>
        </div>
      }
    >
      <div className="p-4 border-t border-gray-300">
        <div className="text-sm font-normal">
          {isEditing ? (
            <HbrTextarea
              className="w-full"
              value={outcome?.data?.description}
              placeholder="Add outcome description"
              maxlength={MAX_DESCRIPTION_LENGTH}
              onHbr-input={(e) => handleInputChange('description', unicodeToPlain(e.target.value))}
            />
          ) : outcome?.data?.description?.length > 0 ? (
            <div>{outcome.data.description}</div>
          ) : (
            <div className="italic text-sm">No description</div>
          )}
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 w-full mt-6 gap-4 border-b border-gray-800 pb-2">
          <div className="text-sm font-semibold">USE CASES</div>
          <div className="text-sm font-semibold">TEST CASES</div>
        </div>

        <div className="flex flex-col">
          {outcome?.children?.length > 0 ? (
            outcome.children.map((id) => (
              <FormSectionUseCase formSectionId={id} key={id} />
            ))
          ) : (
            <div className="text-sm font-normal italic mt-2">No use cases found</div>
          )}

          {![FormComponentChange.Added, FormComponentChange.Deleted].includes(outcome?.change) && (
            <StbButton
              role="add"
              size="medium"
              variant="text"
              onClick={(e) => {
                e.stopPropagation();
                handleAddUseCaseButtonClick();
              }}
              className="mt-4 self-start"
            >
              Add Use Case
            </StbButton>
          )}
        </div>
      </div>
    </StbAccordion>
  );
};

export default FormSectionOutcome;